import { ResponseStatus } from "@/api/apiClient";
import { campaignCouponService } from "@/api/campaignCouponService";
import { campaignService } from "@/api/campaignService";
import { packageCouponService } from "@/api/packageCouponService";
import { createIsoUtcDateTime } from "@/components/utils/dateUtils";
import type { BaseFields } from "@/resources/base";
import type {
  Campaign,
  CampaignLineItemChanges,
  CampaignWithId,
  IndexedCampaignWithId,
  ModifiableCampaignLineItem,
} from "@/resources/campaign";
import type { CampaignCouponWithId, Coupon } from "@/resources/coupon";
import type { PackageWithId } from "@/resources/package";
import type { ScenarioWithId } from "@/resources/scenario";
import { copyPackageCouponsToCampaign } from "./couponUtils";
import { parseDateTime } from "./dateUtils";

const loadedCampaigns: {
  [key: string]: CampaignWithId | null;
} = {};
const loadedCoupons: { [key: string]: CampaignCouponWithId[] } = {};

export async function getCampaignByCode(campaignCode: string) {
  let campaign = loadedCampaigns[campaignCode];

  if (!campaign) {
    const campaigns = await campaignService.list({
      ca: campaignCode,
    });

    if (campaigns.length === 0) {
      return null;
    }

    campaign = campaigns[0];
    loadedCampaigns[campaignCode] = campaign;
  }

  return {
    ...campaign,
    id: campaign.id,
  };
}

export async function getCampaignById(
  campaignId: string,
  forceRefresh = false,
) {
  if (forceRefresh) {
    delete loadedCampaigns[campaignId];
  }

  let campaign = loadedCampaigns[campaignId];

  if (!campaign) {
    campaign = await campaignService.getByIdOrCode(campaignId);

    if (!campaign) {
      return null;
    }

    loadedCampaigns[campaignId] = campaign;
  }

  return {
    ...campaign,
    id: campaign.id,
  };
}

export async function getCampaignCoupons(
  campaignId: string,
): Promise<CampaignCouponWithId[]> {
  if (loadedCoupons[campaignId]) {
    return loadedCoupons[campaignId];
  }

  const coupons = await campaignCouponService.list(campaignId);

  loadedCoupons[campaignId] = coupons;
  return coupons;
}

export async function invalidateCampaignById(campaignId: string) {
  delete loadedCampaigns[campaignId];
}

export function createPackageCampaignPayload({
  packageId,
  campaignCode,
  name,
  scenarioDocId,
  agentGroupId,
  customerCode,
  customerId,
  openAt,
  closedAt,
  startAt,
  endAt,
  limits,
  isUnlimited,
  isDynamic = false,
}: Partial<Omit<CampaignWithId, keyof BaseFields>>): Omit<
  Campaign,
  keyof BaseFields
> {
  if (
    !packageId ||
    !campaignCode ||
    !name ||
    !customerCode ||
    !customerId ||
    !openAt ||
    !closedAt ||
    !startAt ||
    !endAt ||
    (!isDynamic && (!scenarioDocId || !agentGroupId))
  ) {
    throw new Error("Missing required fields");
  }

  return {
    apiKeyId: null,
    packageId: packageId,
    campaignCode: campaignCode,
    name: name,
    scenarioDocId: isDynamic ? "" : (scenarioDocId ?? ""),
    agentGroupId: isDynamic ? "" : (agentGroupId ?? ""),
    category: "",
    customerCode: customerCode,
    customerId: customerId,
    openAt: openAt,
    closedAt: closedAt,
    startAt: startAt,
    endAt: endAt,
    isShow: true,
    isUnlimited: isUnlimited ?? false,
    limits: limits ?? 1,
    couponCount: 0,
    systemInfo: {
      intella: { version: "" },
      gnowsis: { modelVersion: "", systemVersion: "" },
    },
    isDynamic: isDynamic,
  };
}

export async function createPackageCampaign(
  relatedPackage: PackageWithId,
  campaign: CampaignLineItemChanges,
  scenarios: ScenarioWithId[],
  overrideData?: Partial<Omit<Campaign, keyof BaseFields>>,
): Promise<CampaignWithId["id"] | null> {
  if (!relatedPackage || !campaign || !scenarios) {
    return null;
  }

  if (
    !(
      relatedPackage.id &&
      relatedPackage.customerCode &&
      relatedPackage.customerId
    )
  ) {
    throw new Error("Package data is missing required fields");
  }

  const newCampaignId = crypto.randomUUID();

  const scenario = scenarios.find((s) => s.id === campaign.scenarioDocId);
  if (!campaign.isDynamic && !scenario) {
    throw new Error("Scenario not found");
  }

  const campaignPayload = createPackageCampaignPayload({
    packageId: relatedPackage.id,
    campaignCode: newCampaignId,
    name: campaign.name,
    scenarioDocId: campaign.scenarioDocId,
    agentGroupId: scenario?.agentGroupId ?? "",
    isDynamic: campaign.isDynamic,
    customerCode: relatedPackage.customerCode,
    customerId: relatedPackage.customerId,
    openAt: overrideData?.openAt ?? campaign.openAt,
    closedAt: overrideData?.closedAt ?? campaign.closedAt,
    startAt: campaign.startAt,
    endAt: campaign.endAt,
    limits: campaign.limits,
    isUnlimited: campaign.isUnlimited,
  });

  const campaignResponse = await campaignService.create(campaignPayload);
  if (
    campaignResponse.status !== ResponseStatus.Ok ||
    !campaignResponse.result
  ) {
    throw new Error(
      campaignResponse.errorMessage ?? "Failed to create campaign",
    );
  }
  const createdCampaign = campaignResponse.result;

  // update createdCampaign's campaignCode to match its id
  await campaignService.updateByIdOrCode(createdCampaign.id, {
    campaignCode: createdCampaign.id,
  });

  return createdCampaign.id;
}

export function convertCampaignToModifiableCampaignLineItem(
  campaign: IndexedCampaignWithId,
  index: number,
  packageOpenAt?: string | number | null,
  packageClosedAt?: string | number | null,
): ModifiableCampaignLineItem {
  const startAtParsed = parseDateTime(campaign.startAt);
  const endAtParsed = parseDateTime(campaign.endAt);

  let isSameAsViewingPeriod = false;
  if (packageOpenAt != null && packageClosedAt != null) {
    const viewingStart = parseDateTime(packageOpenAt);
    const viewingEnd = parseDateTime(packageClosedAt);
    isSameAsViewingPeriod =
      startAtParsed.date === viewingStart.date &&
      startAtParsed.time === viewingStart.time &&
      endAtParsed.date === viewingEnd.date &&
      endAtParsed.time === viewingEnd.time;
  }

  return {
    id: campaign.id,
    name: campaign.name,
    scenarioDocId: campaign.scenarioDocId,
    isDynamic: campaign.isDynamic,
    isSameAsViewingPeriod,
    startInterviewDate: startAtParsed?.date,
    startInterviewTime: startAtParsed?.time,
    endInterviewDate: endAtParsed?.date,
    endInterviewTime: endAtParsed?.time,
    errorMessage: "",
    limits: campaign.isUnlimited ? null : (campaign.limits ?? 1),
    limitMenuOpen: false,
    index: index,
  };
}

export async function createNewCampaignWithCoupons(
  campaign: CampaignLineItemChanges,
  relatedPackage: PackageWithId | null,
  packageCoupons: Coupon[],
  scenarios: ScenarioWithId[],
  overrideData?: Partial<Omit<Campaign, keyof BaseFields>>,
) {
  if (!relatedPackage) return;

  const campaignId = await createPackageCampaign(
    relatedPackage,
    campaign,
    scenarios,
    {
      openAt: overrideData?.openAt ?? campaign.openAt,
      closedAt: overrideData?.closedAt ?? campaign.closedAt,
    },
  );
  if (!campaignId) {
    throw new Error(`Failed to create campaign: ${campaign.name}`);
  }

  campaign.id = campaignId;
  try {
    await copyPackageCouponsToCampaign(
      packageCoupons,
      relatedPackage.id,
      campaignId,
    );
  } catch (error) {
    console.error("Failed to copy package coupons to campaign:");
  }
}

export async function saveExistingCampaigns(
  campaignUpdates: CampaignLineItemChanges[],
  overrideData?: Partial<Omit<Campaign, keyof BaseFields>>,
) {
  const campaignsToUpdate = campaignUpdates.filter(
    (campaign) => campaign.operation === "update",
  );
  if (campaignsToUpdate.length === 0) return;

  const campaignUpdatePayloads = campaignsToUpdate.map((campaign) => {
    return {
      id: campaign.id,
      name: campaign.name,
      scenarioDocId: campaign.isDynamic ? "" : campaign.scenarioDocId,
      agentGroupId: campaign.isDynamic ? "" : campaign.agentGroupId,
      isDynamic: campaign.isDynamic ?? false,
      startAt: campaign.startAt,
      endAt: campaign.endAt,
      isUnlimited: campaign.isUnlimited ?? false,
      limits: campaign.limits ?? 1,
      openAt: overrideData?.openAt ?? campaign.openAt,
      closedAt: overrideData?.closedAt ?? campaign.closedAt,
    };
  });

  const results = await Promise.all(
    campaignUpdatePayloads.map(async (campaign) => {
      if (!campaign.id) {
        throw new Error("Campaign ID is required");
      }
      const response = await campaignService.updateByIdOrCode(
        campaign.id,
        campaign,
      );
      if (response.status !== ResponseStatus.Ok || !response.result) {
        throw new Error(
          response.errorMessage ?? `Failed to update campaign ${campaign.id}`,
        );
      }
      return response;
    }),
  );
  return results;
}

export async function saveNewCampaigns(
  campaignUpdates: CampaignLineItemChanges[],
  relatedPackage: PackageWithId | null,
  scenarios: ScenarioWithId[],
  overrideData?: Partial<Omit<Campaign, keyof BaseFields>>,
) {
  if (!relatedPackage) return;
  const newCampaigns = campaignUpdates.filter(
    (campaign) => campaign.operation === "create",
  );
  if (newCampaigns.length === 0) return;

  // Refetch all package coupons to ensure the latest data is used
  const allPackageCoupons = await packageCouponService.list(relatedPackage.id);

  for (const campaign of newCampaigns) {
    await createNewCampaignWithCoupons(
      campaign,
      relatedPackage,
      allPackageCoupons,
      scenarios,
      overrideData,
    );
  }
}

export function prepareForSaving(
  campaign: ModifiableCampaignLineItem,
  operation: "create" | "update" | "delete" | "none",
): CampaignLineItemChanges {
  const isUnlimited = campaign.limits === null;
  return {
    id: campaign.id ?? null,
    index: campaign.index ?? 0,
    name: campaign.name,
    scenarioDocId: campaign.scenarioDocId,
    agentGroupId: campaign.agentGroupId,
    isDynamic: campaign.isDynamic ?? false,
    startAt: createIsoUtcDateTime(
      campaign.startInterviewDate ?? null,
      campaign.startInterviewTime ?? "",
    ),
    endAt: createIsoUtcDateTime(
      campaign.endInterviewDate ?? null,
      campaign.endInterviewTime ?? "",
    ),
    isUnlimited,
    limits: isUnlimited ? 0 : (campaign.limits ?? 1),
    operation,
  };
}
